import { styled } from '@mui/material/styles';

export const WorkflowContainer = styled('div')({
    width: '100%',
    margin: '0 auto'
});

const getMarginLeft = () => '-2%';

const getStepperWidth = ($stepCount?: number) =>
    `calc(100% / ${($stepCount || 4) - 0.16 * ($stepCount || 4)})`;

export const WorkflowSteps = styled('div')({
    display: 'flex',
    alignItems: 'stretch',
    height: '60px',
    position: 'relative',
    marginLeft: '-2.6rem',
    marginRight: '-2rem',
    marginTop: '-0.71rem'
});

interface StepProps {
    $isFirst?: boolean;
    $isLast?: boolean;
    $status?: 'completed' | 'active' | 'pending' | 'error';
    $disabled?: boolean;
    $isPendingActive?: boolean;
    $isError?: boolean;
    $stepCount?: number;
    $stepIndex?: number;
}

export const Step = styled('div')<StepProps>(
    ({
        theme,
        $isFirst,
        $disabled,
        $status,
        $isPendingActive,
        $isError,
        $stepCount,
        $stepIndex
    }) => ({
        position: 'relative',
        width: getStepperWidth($stepCount),
        display: 'flex',
        alignItems: 'center',
        cursor: 'default',
        overflow: 'visible',
        marginLeft: $isFirst ? 0 : getMarginLeft(),
        transition: 'all 0.2s ease',
        zIndex: $isError ? 21 : 20 - ($stepIndex || 0),

        // Pending disabled styles
        ...($status === 'pending' &&
            $disabled && {
                '& .step-shape svg path': {
                    fill: theme.palette.semantic.bg.raised,
                    stroke: theme.palette.semantic.bg.raised
                },
                '& .step-status.pending svg path': {
                    fill: theme.palette.semantic.icon.subtle
                },
                '& .step-text': {
                    color: theme.palette.semantic.icon.subtle
                },
                '& .step-icon svg path': {
                    fill: theme.palette.semantic.icon.subtle,
                    stroke: theme.palette.semantic.icon.subtle,
                    strokeWidth: 0
                },
                // Mirror the path rule for icons built from <circle> elements
                // (e.g. execution.svg) so they also appear disabled.
                '& .step-icon svg circle': {
                    stroke: theme.palette.semantic.icon.subtle
                },
                '& .step-status svg path': {
                    stroke: theme.palette.semantic.icon.subtle,
                    strokeWidth: 0
                }
            }),

        // Pending active styles
        ...($isPendingActive &&
            !$isError && {
                '& .step-shape svg path': {
                    fill: theme.palette.semantic.bg.brandDark,
                    stroke: theme.palette.semantic.bg.brandDark
                },
                '& .step-status.pending svg path': {
                    fill: theme.palette.semantic.icon.inverse
                },
                '& .step-text': {
                    color: theme.palette.semantic.text.inverse
                },
                '& .step-icon svg path': {
                    fill: theme.palette.semantic.icon.inverse,
                    color: theme.palette.semantic.icon.inverse
                },
                '& .step-icon svg circle': {
                    stroke: theme.palette.semantic.icon.inverse
                }
            }),

        // Pending active error styles
        ...($isPendingActive &&
            $isError && {
                '& .step-shape svg path': {
                    fill: theme.palette.semantic.bg.default,
                    stroke: theme.palette.semantic.bd.error
                },
                '& .step-status.pending svg path': {
                    fill: theme.palette.semantic.icon.error
                },
                '& .step-text': {
                    color: theme.palette.semantic.int.negative
                },
                '& .step-icon svg path': {
                    fill: theme.palette.semantic.icon.error
                },
                '& .step-icon svg circle': {
                    stroke: theme.palette.semantic.icon.error
                }
            }),

        // Completed status styles
        ...($status === 'completed' && {
            '& .step-status': {
                border: 'none'
            },
            '& .step-status.completed svg path': {
                fill: theme.palette.semantic.bd.brand
            }
        }),

        // Active status styles
        ...($status === 'active' && {
            '& .step-status': {
                border: 'none'
            },
            '& .step-content': {
                color: theme.palette.semantic.text.inverse
            },
            '& .step-icon svg path': {
                fill: theme.palette.semantic.icon.inverse,
                color: theme.palette.semantic.icon.inverse
            },
            '& .step-icon svg circle': {
                stroke: theme.palette.semantic.icon.inverse
            },
            '& .step-text': {
                color: theme.palette.semantic.text.inverse
            }
        }),

        // Error status styles
        ...($status === 'error' && {
            '& .step-content': {
                color: theme.palette.semantic.int.negative
            },
            '& .step-icon svg path': {
                fill: theme.palette.semantic.icon.error
            },
            '& .step-text': {
                color: theme.palette.semantic.int.negative
            },
            '& .step-icon svg circle': {
                stroke: theme.palette.semantic.icon.error
            }
        }),

        // Active error styles (combined)
        ...($status === 'active' &&
            $isError && {
                '& .step-text': {
                    color: theme.palette.semantic.int.negative
                },
                '& .step-icon svg path': {
                    fill: theme.palette.semantic.icon.error
                },
                '& .step-icon svg circle': {
                    stroke: theme.palette.semantic.icon.error
                }
            }),

        // Pending status styles
        ...($status === 'pending' &&
            !$isPendingActive && {
                '& .step-content': {
                    color: theme.palette.neutral.black
                },
                ...(!$disabled && {
                    '& .step-status.pending svg path': {
                        fill: theme.palette.semantic.icon.brand
                    }
                })
            }),

        // Hover effects
        ...(!$disabled && {
            '&:hover .step-text': {
                textDecoration: 'underline'
            }
        })
    })
);

export const StepShape = styled('div')({
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    overflow: 'visible',
    '& svg': {
        width: '100%',
        height: '100%',
        overflow: 'visible'
    }
});

export const StepContent = styled('div')<{ $isFirst?: boolean; $isLast?: boolean }>(({ $isFirst, $isLast }) => ({
    position: 'relative',
    zIndex: 10,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '0 35px 0 60px',
    width: '100%',
    height: '100%',
    ...($isFirst && {
        paddingLeft: '20px'
    }),
    ...($isLast && {
        paddingRight: '20px'
    })
}));

export const StepIcon = styled('div')({
    width: '30px',
    height: '30px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    fontSize: '14px'
});

export const StepText = styled('div')<{ $isFirst?: boolean }>(({ $isFirst }) => ({
    whiteSpace: $isFirst ? 'normal' : 'nowrap',
    fontSize: '14px',
    fontWeight: 500,
    flexGrow: 1
}));

export const StepStatus = styled('div')({});
